import * as React from "react";
import styles from "./styles.module.scss";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Input } from "components/form";
import { useToast } from "hooks/useToast";
import { SendIcon, UploadIconTwo, DocsIcon, XIcon } from "assets";
import { isTalentAudit } from "helpers/assistantFlow";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  AUDIT_DOCUMENT_ACCEPT,
  isSupportedComposerFile,
  PDF_DOCUMENT_ACCEPT,
} from "./file-types";
import { shouldUseDocumentComposer } from "helpers/assistantInputHints";

// Register custom divider (horizontal rule) blot
const BlockEmbed = Quill.import('blots/block/embed') as any;
class DividerBlot extends BlockEmbed {
  static blotName = 'divider';
  static tagName = 'hr';
}
Quill.register(DividerBlot);

interface MessageFormData {
  message: string;
}

interface MessageFormProps {
  onSendMessage: (message: string, attachments?: File[]) => void;
  placeholder?: string;
  className?: string;
  conversationState?: string;
  maxFiles?: number;
  /** Hide the attachment control when the assistant only wants a typed answer. */
  allowUpload?: boolean;
  /** Use the rich editor and treat pasted long-form content as a document. */
  documentMode?: boolean;
  /** Pre-populate the (plain-text) input once on mount, e.g. a carried-over role. */
  initialValue?: string;
}

const messageToolbarModules = {
  toolbar: {
    container: "#message-toolbar",
    handlers: {
      undo: function (this: any) {
        this.quill.history.undo();
      },
      redo: function (this: any) {
        this.quill.history.redo();
      },
      hr: function (this: any) {
        const range = this.quill.getSelection();
        if (range) {
          this.quill.insertText(range.index, '\n');
          this.quill.insertEmbed(range.index + 1, 'divider', true);
          this.quill.setSelection(range.index + 2);
        }
      },
    },
  },
  history: {
    delay: 1000,
    maxStack: 100,
    userOnly: true,
  },
};

const messageFormats = [
  "header",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "code-block",
  "code",
  "list",
  "bullet",
  "indent",
  "link",
  "align",
  "divider",
];

const MessageFormToolbar = () => (
  <div className={styles.messageToolbar} id="message-toolbar">
    <span className="ql-formats">
      <button className="ql-undo" title="Undo">
        <svg viewBox="0 0 18 18" width="18" height="18">
          <path d="M6 3L2 7l4 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 7h9a4 4 0 0 1 0 8H9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <button className="ql-redo" title="Redo">
        <svg viewBox="0 0 18 18" width="18" height="18">
          <path d="M12 3l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 7H7a4 4 0 0 0 0 8h2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </span>

    <span className="ql-formats">
      <select className="ql-size" defaultValue="medium">
        <option value="small">Small size</option>
        <option value="medium">Medium size</option>
        <option value="large">Large size</option>
        <option value="huge">Huge size</option>
      </select>
    </span>

    <span className="ql-formats">
      <select className="ql-align" />
    </span>

    <span className="ql-formats">
      <button className="ql-bold" />
      <button className="ql-italic" />
      <button className="ql-underline" />
      <button className="ql-strike" />
    </span>

    <span className="ql-formats">
      <button className="ql-code" />
    </span>

    <span className="ql-formats">
      <button className="ql-list" value="bullet" />
      <button className="ql-list" value="ordered" />
    </span>

    <span className="ql-formats">
      <button className="ql-link" />
      <button className="ql-code-block" />
      <button className="ql-blockquote" />
    </span>

    <span className="ql-formats">
      <button className="ql-hr" title="Horizontal rule">
        <svg viewBox="0 0 18 18" width="18" height="18">
          <line x1="2" y1="9" x2="16" y2="9" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>
    </span>
  </div>
);

const MessageForm: React.FC<MessageFormProps> = ({
  onSendMessage,
  placeholder = "Type your message...",
  className = "",
  conversationState,
  maxFiles = 5,
  allowUpload = true,
  documentMode = false,
  initialValue,
}) => {
  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [editorContent, setEditorContent] = React.useState("");
  const [isDragOver, setIsDragOver] = React.useState(false);
  const { openToast } = useToast();
  const isAuditFlow = isTalentAudit();
  const acceptedFileTypes = isAuditFlow ? AUDIT_DOCUMENT_ACCEPT : PDF_DOCUMENT_ACCEPT;

  const filterSupportedFiles = (files: File[]): File[] => {
    const supportedFiles = files.filter((file) => isSupportedComposerFile(file, isAuditFlow));
    if (supportedFiles.length < files.length) {
      openToast({
        show: true,
        heading: "Unsupported file",
        text: isAuditFlow
          ? "Only PDF, DOC, and DOCX documents are allowed."
          : "Only PDF documents are allowed. Please upload a PDF file.",
        type: "error",
        timeOut: 5000,
      });
    }
    return supportedFiles;
  };

  // API-driven document mode covers every current/future Flow 3 upload prompt.
  // The helper retains the two legacy JD states used by older hiring responses.
  const useRichEditor = shouldUseDocumentComposer(conversationState, documentMode);
  const isRoleInput = conversationState === 'get_role';
  const maxCharLimit = isRoleInput ? 100 : undefined;

  // Drag-and-drop is enabled for JD and CV upload stages
  const isDragDropEnabled =
    (isAuditFlow && allowUpload) ||
    conversationState === 'upload_jd' ||
    conversationState === 'get_jd_upload' ||
    conversationState === 'upload_cv' ||
    conversationState === 'upload_cvs' ||
    conversationState === 'get_cv_upload' ||
    conversationState === 'upload_resume';

  const handleDragOver = (e: React.DragEvent<HTMLElement>) => {
    if (!isDragDropEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLElement>) => {
    if (!isDragDropEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLElement>) => {
    if (!isDragDropEnabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const droppedFiles = filterSupportedFiles(Array.from(e.dataTransfer.files));
    if (droppedFiles.length > 0) {
      const remaining = maxFiles - uploadedFiles.length;
      setUploadedFiles((prev) => [...prev, ...droppedFiles.slice(0, remaining)]);
    }
  };

  const getValidationSchema = () => {
    let messageSchema = yup
      .string()
      .default('')
      .test(
        'not-empty-if-no-files',
        'Message or file required',
        function (value) {
          if (useRichEditor) {
            return (editorContent && editorContent.trim().length > 0) || uploadedFiles.length > 0;
          }
          return (value && value.trim().length > 0) || uploadedFiles.length > 0;
        }
      );

    if (maxCharLimit) {
      messageSchema = messageSchema.max(maxCharLimit, `Maximum ${maxCharLimit} characters allowed`);
    }

    return yup.object().shape({
      message: messageSchema,
    });
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<MessageFormData>({
    resolver: yupResolver(getValidationSchema()),
    mode: 'onSubmit'
  });

  const messageValue = watch('message') || '';

  // Apply a carried-over value (e.g. the role typed on the landing page) once
  // per mount, letting the user review/edit before sending. Guarded so it never
  // overwrites edits the user makes afterwards. The caller keeps the value alive
  // until it is sent, so a remount of this form re-applies it rather than coming
  // back empty.
  const hasAppliedInitialValue = React.useRef(false);
  React.useEffect(() => {
    if (hasAppliedInitialValue.current) return;
    if (!initialValue) return;
    hasAppliedInitialValue.current = true;
    reset({ message: initialValue });
  }, [initialValue, reset]);

  const handleFormSubmit = (data: MessageFormData) => {
    if (useRichEditor) {
      const plainText = htmlToPlainText(editorContent);
      if (!plainText.trim() && uploadedFiles.length === 0) return;
      onSendMessage(plainText, uploadedFiles.length > 0 ? [...uploadedFiles] : undefined);
      setEditorContent("");
    } else {
      if (!data.message.trim() && uploadedFiles.length === 0) return;
      onSendMessage(data.message, uploadedFiles.length > 0 ? [...uploadedFiles] : undefined);
    }
    reset();
    setUploadedFiles([]);
  };

  const htmlToPlainText = (html: string): string => {
    let text = html;
    // Convert block elements to newlines
    text = text.replace(/<\/?(h[1-6]|p|div|li|ul|ol|tr|blockquote|hr)[^>]*>/gi, '\n');
    // Convert <br> to newlines
    text = text.replace(/<br\s*\/?>/gi, '\n');
    // Remove remaining HTML tags
    text = text.replace(/<[^>]+>/g, '');
    // Decode HTML entities
    const doc = new DOMParser().parseFromString(text, 'text/html');
    text = doc.body.textContent || '';
    // Collapse multiple blank lines into double newlines
    text = text.replace(/\n{3,}/g, '\n\n');
    return text.trim();
  };

  const handleRichEditorSubmit = () => {
    const plainText = htmlToPlainText(editorContent);
    if (!plainText.trim() && uploadedFiles.length === 0) return;
    onSendMessage(plainText, uploadedFiles.length > 0 ? [...uploadedFiles] : undefined);
    setEditorContent("");
    setUploadedFiles([]);
  };

  const handleEditorChange = (content: string) => {
    if (content === '<p><br></p>') {
      setEditorContent('');
    } else {
      setEditorContent(content);
    }
  };

  const isAtFileLimit = uploadedFiles.length >= maxFiles;

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newFiles = filterSupportedFiles(Array.from(files));
      const remainingSlots = maxFiles - uploadedFiles.length;
      const filesToAdd = newFiles.slice(0, remainingSlots);
      if (filesToAdd.length > 0) {
        setUploadedFiles((prevFiles) => [...prevFiles, ...filesToAdd]);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((_, i) => i !== index)
    );
  };

  const triggerFileInput = () => {
    if (isAtFileLimit) return;
    fileInputRef.current?.click();
  };

  if (useRichEditor) {
    return (
      <div
        className={`${styles.messageForm} ${styles.richEditorForm} ${className}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ position: 'relative' }}
      >
        {isDragOver && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(235, 240, 255, 0.92)',
              border: '2px dashed #012169',
              borderRadius: '12px',
              pointerEvents: 'none',
            }}
          >
            <p style={{ color: '#012169', fontWeight: 600, fontSize: '16px' }}>Drop file here</p>
          </div>
        )}
        {uploadedFiles.length > 0 && (
          <DocumentUpload
            files={uploadedFiles}
            onRemove={handleRemoveFile}
          />
        )}

        <div className="text-editor">
          <MessageFormToolbar />
          <ReactQuill
            className={styles.richEditorQuill}
            style={{ height: '10rem' }}
            value={editorContent}
            onChange={handleEditorChange}
            placeholder={placeholder}
            modules={messageToolbarModules}
            formats={messageFormats}
          />
        </div>

        <input
          type="file"
          {...(maxFiles > 1 ? { multiple: true } : {})}
          ref={fileInputRef}
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          accept={acceptedFileTypes}
        />
        <div className={styles.inputIcons}>
          {allowUpload && (
            <div className={styles.uploadIconWrap}>
              <div
                className={`${styles.uploadIcon} ${isAtFileLimit ? styles.uploadIconDisabled : ''}`}
                onClick={triggerFileInput}
                title={isAtFileLimit ? `Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} reached` : 'Upload file'}
              >
                <UploadIconTwo />
              </div>
              {uploadedFiles.length > 0 && maxFiles > 1 && (
                <span className={styles.fileCount}>{uploadedFiles.length}/{maxFiles}</span>
              )}
            </div>
          )}
          <div className={styles.uploadIcon}>
            <SendIcon onClick={handleRichEditorSubmit} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      className={`${styles.messageForm} ${className}`}
      onSubmit={handleSubmit(handleFormSubmit)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{ position: 'relative' }}
    >
      {isDragOver && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(235, 240, 255, 0.92)',
            border: '2px dashed #012169',
            borderRadius: '12px',
            pointerEvents: 'none',
          }}
        >
          <p style={{ color: '#012169', fontWeight: 600, fontSize: '16px' }}>Drop file here</p>
        </div>
      )}
      {uploadedFiles.length > 0 && (
        <DocumentUpload
          files={uploadedFiles}
          onRemove={handleRemoveFile}
        />
      )}

      <Input
        type="text"
        name="message"
        placeholder={placeholder}
        className={styles.messageInput}
        parentClassName={styles.inputWrapper}
        register={register}
        validatorMessage={errors.message?.message}
        maxLength={maxCharLimit}
      />
      {maxCharLimit && (
        <span className={styles.charCounter}>
          {messageValue.length}/{maxCharLimit}
        </span>
      )}
      <input
        type="file"
        {...(maxFiles > 1 ? { multiple: true } : {})}
        ref={fileInputRef}
        onChange={handleFileUpload}
        style={{ display: 'none' }}
        accept={acceptedFileTypes}
      />
      <div className={styles.inputIcons}>
        {allowUpload && (
          <div className={styles.uploadIconWrap}>
            <div
              className={`${styles.uploadIcon} ${isAtFileLimit ? styles.uploadIconDisabled : ''}`}
              onClick={triggerFileInput}
              title={isAtFileLimit ? `Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} reached` : 'Upload file'}
            >
              <UploadIconTwo />
            </div>
            {uploadedFiles.length > 0 && maxFiles > 1 && (
              <span className={styles.fileCount}>{uploadedFiles.length}/{maxFiles}</span>
            )}
          </div>
        )}
        <div className={styles.uploadIcon}>
          <SendIcon onClick={handleSubmit(handleFormSubmit)} />
        </div>
      </div>
    </form>
  );
};

export { MessageForm };

export const DocumentUpload: React.FC<{
  files: File[];
  onRemove: (index: number) => void;
}> = ({ files, onRemove }) => {
  return (
    <div className="max-w-full overflow-x-auto">
      <div className={`w-full !flex !flex-row !pt-2 ${styles.uploadedDocuments}`}>
        {files.map((file, index) => (
          <div key={index} className={styles.documentItem}>
            <div className={styles.documentInfo}>
              <div className={styles.documentIcon}>
                <DocsIcon style={{ color: "#FFFFFF" }} />
              </div>
              <div>
                <p className={styles.documentName}>{file.name}</p>
                <p className={styles.documentTitle}>Document</p>
              </div>
            </div>
            <XIcon
              className={styles.removeButton}
              type="button"
              onClick={() => onRemove(index)}
              aria-label="Remove document"
            />
          </div>
        ))}
      </div>
    </div>

  );
};
